/**
 * Single source of display formatting — dates, hours (#739).
 *
 * `utils/date-utils.ts` stays responsible for the computation (parsing, arithmetic, week bounds);
 * this module only turns an already computed value into on-screen text. After this work, no call
 * to `toLocaleDateString`, `Intl.*` or a display `toFixed` should exist anywhere else in `src/` —
 * if any are left, they have a local reason documented where they are.
 *
 * Locale: `fr-CA` everywhere, without exception (two calls used `fr-FR`, one `en-CA`: migrating to
 * these functions fixes them on the way — the rendered text does not change, see the tests).
 *
 * Time-zone note (#741): the `Date`s received here are local dates built by
 * `parseDateStringAsLocal` / `combineDateAndTime` — a pure calendar (year/month/day), not a UTC
 * instant. Reading them (`getFullYear`/`getMonth`/`getDate`, used internally by
 * `toLocaleDateString`) therefore happens in the same local clock as the one that built them.
 * Pinning `timeZone: 'America/Toronto'` on the calls below would break that consistency on a
 * machine set to another time zone (the calendar read would no longer be the one that was built) —
 * exactly the bug of #741, moved from writing to reading. That is why this file deliberately sets
 * no time zone on its `Intl`/`toLocaleDateString` calls: the time zone of Parcours users
 * (`America/Toronto`, exported below for reference) is already the one in which those `Date`s were
 * made. A function receiving a genuine server instant (a timestamp, not a calendar date) should
 * rather go through the SDK's `getUtilsSdk()`, which handles that case correctly.
 */

import { parseDateStringAsLocal } from '@/utils/date-utils';
import { roundHours } from '@/components/presence-hours';

const LOCALE = 'fr-CA';

/** The time zone of Parcours users — see the time-zone note above for why it is not passed to
 *  this file's `Intl`/`toLocaleDateString` calls. */
export const TIME_ZONE = 'America/Toronto';

/**
 * Formats a date string in the given locale (moved from `utils/date-utils.ts:formatDateString`,
 * #739 — behaviour unchanged, 8 callers).
 * @param dateString - Date string to format
 * @param locale - Locale to use (default: 'fr-CA')
 * @returns Formatted date string, or '' when `dateString` is empty
 */
export function formatDateString(dateString: string | undefined, locale: string = LOCALE): string {
  if (!dateString) return '';
  const date = parseDateStringAsLocal(dateString);
  return date.toLocaleDateString(locale);
}

/** `31/12/2025`-like default rendering of `toLocaleDateString('fr-CA')` with no options — « 2025-12-31 ». */
export function formatDateNumeric(date: Date): string {
  return date.toLocaleDateString(LOCALE);
}

/** Default rendering in a grid: « 31 déc. 2025 ». */
export function formatDateShort(date: Date): string {
  return date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** « 31 décembre 2025 ». */
export function formatDateLong(date: Date): string {
  return date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
}

/** « mercredi 31 décembre 2025 ». */
export function formatDateWeekday(date: Date): string {
  return date.toLocaleDateString(LOCALE, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

/** « mercredi 31 décembre » — like `formatDateWeekday`, without the year. */
export function formatDateWeekdayShort(date: Date): string {
  return date.toLocaleDateString(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' });
}

/** « mercredi » seul. */
export function formatWeekdayLong(date: Date): string {
  return date.toLocaleDateString(LOCALE, { weekday: 'long' });
}

/** « 31 déc. » — day + abbreviated month, no year. */
export function formatDateDayMonthShort(date: Date): string {
  return date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' });
}

/** « 31 décembre » — day + full month, no year. */
export function formatDayMonthLong(date: Date): string {
  return date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long' });
}

/** « 31 déc. – 14 janv. 2026 » — abbreviated start with no year, abbreviated end with year. */
export function formatDateRange(start: Date, end: Date): string {
  return `${formatDateDayMonthShort(start)} – ${formatDateShort(end)}`;
}

/** « 31 déc. – 14 janv. » — abbreviated start and end, neither showing the year. */
export function formatDateRangeShort(start: Date, end: Date): string {
  return `${formatDateDayMonthShort(start)} – ${formatDateDayMonthShort(end)}`;
}

/** « 05 » — day of the month on two digits. */
export function formatDayOfMonth(date: Date): string {
  return date.toLocaleDateString(LOCALE, { day: '2-digit' });
}

/** « DÉC » — abbreviated month, full stop removed, upper-cased. */
export function formatMonthAbbrUpper(date: Date): string {
  return date.toLocaleDateString(LOCALE, { month: 'short' }).replace('.', '').toUpperCase();
}

/**
 * Formats a duration in hours with `Intl.NumberFormat`, followed by « h » (#739 — two copies,
 * `work-placements.vue` / `my-work-placement.vue`, differing only in the number of decimals).
 */
export function formatHoursIntl(hours: number, maximumFractionDigits: number): string {
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits }).format(hours)} h`;
}

/**
 * Formats a duration the French way: 7.5 → « 7,50 » (moved from `components/presence-hours.ts`,
 * #739 — this is the reference tested implementation, body unchanged).
 */
export function formatHours(hours: number): string {
  return roundHours(hours).toFixed(2).replace('.', ',');
}
